// The read-only / momentary-write indicator (Brief 5 / pt7 + pt9) — the
// persistent, calm proof that Waytide is operating READ-ONLY almost all the
// time, flipping briefly to a NAMED write state for a genuine write (the hook
// injection, or a steer that reached the agent), then receding.
//
// Shown, not claimed (pt9): at rest it reads "read-only" beside a faint count of
// the genuine writes this session ("read-only · 2 writes"), so the 99% ratio is
// FELT as a number rather than asserted. The count persists after the momentary
// flash recedes, so a write leaves a trace instead of vanishing.
//
// Honesty: the posture is resolved from CONFIRMED facts upstream (a
// landed status transition / a real bridge attach), never an optimistic click —
// this component only renders the resolved `Posture`. When the session isn't
// confirmed-attached it shows "watching", never a read-only it can't back.
//
// Calm + a11y: the resting pill is a plain `aria-label`led element, NOT
// a live region (so a screen reader isn't re-read "read-only" on every paint); a
// SEPARATE visually-hidden `aria-live="polite"` region announces a genuine write
// once, then clears. Token-pure: read-only = signal-stopped grey; write =
// signal-waiting (a transient, intentional, non-alarm state) — NEVER the reserved
// signal-attention amber ("needs you" stays exclusive). The momentary swap is a
// content change, not an animation, and is stilled-correct under reduced-motion.

import type { ResolvedPosture } from "../model/readWritePosture";
import "../styles/readWritePosture.css";

/** The plain "N writes" suffix for the felt ratio, or "" at zero (a session that
 * has never been written to reads simply "read-only" — the cleanest truth). */
function writeCountLabel(n: number): string {
  if (n <= 0) return "";
  return n === 1 ? "1 write" : `${n} writes`;
}

/** The screen-reader announcement for a genuine write — one polite line, what +
 * where, e.g. "write: paused the agent in o/r". Empty unless a write is active. */
function announce(resolved: ResolvedPosture): string {
  const w = resolved.active;
  if (resolved.posture !== "write" || w == null) return "";
  return w.where ? `write: ${w.what} in ${w.where}` : `write: ${w.what}`;
}

export function ReadOnlyIndicator({ resolved }: { resolved: ResolvedPosture }) {
  const { posture, writeCount, active } = resolved;
  const count = writeCountLabel(writeCount);

  return (
    <span className="wt-rw" data-posture={posture}>
      <span
        className="wt-rw__pill"
        data-posture={posture}
        // The resting pill is a label, not a live region — it must not re-announce
        // on every render. The write event is announced by the live region below.
        aria-label={
          posture === "write" && active
            ? `writing: ${active.what}`
            : posture === "unknown"
              ? "watching"
              : "read-only"
        }
      >
        <span className="wt-rw__dot" aria-hidden="true" />
        <span className="wt-rw__label">
          {posture === "write" && active ? (
            // A genuine write: name it (what + where). Stays calm; never amber.
            <>
              <span className="wt-rw__what">{active.what}</span>
              {active.where ? (
                <span className="wt-rw__where"> · {active.where}</span>
              ) : null}
            </>
          ) : posture === "unknown" ? (
            "watching"
          ) : (
            <>
              read-only
              {count ? <span className="wt-rw__count"> · {count}</span> : null}
            </>
          )}
        </span>
      </span>

      {/* The polite, visually-hidden announcement: a genuine write is read once to
          a screen reader, then cleared (no spam). The resting posture is NOT in
          here, so "read-only" is never re-read on a paint. */}
      <span className="wt-sr-only" role="status" aria-live="polite">
        {announce(resolved)}
      </span>
    </span>
  );
}
